import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { useStore } from "../../../contexts/store"
import { navigate } from "wouter/use-browser-location"
import { PodClass } from "../../../classes/Pod"

import ActivityPreview from "./ActivityPreview"
import ConversationsPreview from "./ConversationsPreview"
import MaterialPreview from "./MaterialPreview"
import NotesPreview from "./NotesPreview"
import PodImageHeader from "../PodImageHeader"
import ReadingQuestionsPreview from "./ReadingQuestionsPreview"
import TagsPreview from "./TagsPreview"

import { Box, Button, Card } from "@mui/material"
import ErrorBoundaryComponent from "../../ErrorBoundaryComponent"


const PodDashboard = ({pod}: {pod: PodClass}) => {
  const { t } = useTranslation()
  const { uiStore } = useStore()
  const deviceWidth = uiStore.deviceWidth
  const hasImageHeader = uiStore.hasImageHeader
  const paddingContainer = uiStore.paddingContainer
  const views = ['contents', 'conversations', 'notes', 'activity', 'questions', 'tags']

  function getViewTitle(view: string) {
    if(view === 'contents') return 'Materials'
    if(view === 'conversations') return 'My Conversations'
    if(view === 'notes') return 'Notes'
    if(view === 'activity') return 'Activity'
    if(view === 'questions') return 'Reading questions'
    if(view === 'tags') return 'Tags'
    console.warn("no translation available for view", view)
    return ""
  }

  function navigateToView(view: string) {
    if(pod.podId && view) {
      const url = `/pod/${pod.podId}/${view}/`
      navigate(url, {state: {prevUrl: window.location.pathname}})
    }
  }

  const PodCard = ({view}: {view: string}) => {
    const viewTitle = getViewTitle(view)
    let viewContentPreview = null
    if(view === "activity") viewContentPreview = <ActivityPreview pod={pod} />
    if(view === "contents") viewContentPreview = <MaterialPreview podId={pod.podId} />
    if(view === "conversations") viewContentPreview = <ConversationsPreview podId={pod.podId} />
    if(view === "notes") viewContentPreview = <NotesPreview pod={pod} />
    if(view === "questions") viewContentPreview = <ReadingQuestionsPreview pod={pod} />
    if(view === "tags") viewContentPreview = <TagsPreview pod={pod} />

    return (
      <Card sx={{
        borderRadius: "20px",
        minHeight: "200px",
        width: "100%"
      }}>
        <ErrorBoundaryComponent context={"Pod>Dashboard>"+viewTitle}>
        {/* header */}
        <Box sx={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          padding: "15px"
        }}>
          <Box sx={{
            color: "textcolor.main",
            flexGrow: 1,
            fontSize: "18px",
            fontWeight: 600,
            margin: "10px 15px"
          }}>
            {t(viewTitle)}
          </Box>
          <Button variant="contained" sx={{borderRadius: "10px"}} onClick={() => navigateToView(view)}>
            {t('All {{view}}', {view: t(viewTitle)})}
          </Button>
        </Box>
        {viewContentPreview}
        </ErrorBoundaryComponent>
      </Card>
    )
  }

  return (
    <Box sx={{
      display: "grid",
      gap: "15px",
      overflowY: "scroll",
      padding: paddingContainer
    }}>
      {hasImageHeader &&
        <PodImageHeader />
      }
      <Box sx={{
        display: "grid",
        gap: "20px",
        gridTemplateColumns: deviceWidth === "tiny" ? 'repeat(1, minmax(200, 1fr))' : 'repeat(auto-fit, minmax(425px, 1fr))',
        justifyItems: "center",
        margin: "5px 0"
      }}>
        {views.map(view => (
          <PodCard key={view} view={view} />
        ))}
      </Box>
    </Box>
  )
}

export default observer(PodDashboard)
